import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { InfoHint } from './info-hint'
import { cn } from 'src/utils/style'

interface InlineHelpProps {
  /** The single plain-language sentence shown by default. Keep it to one short
   * sentence and free of raw provider jargon. Omit when the surrounding copy
   * already states the sentence and only the "Why?" affordance is needed. */
  text?: ReactNode
  /** The fuller explanation revealed on demand — never shown until the user asks. */
  detail: ReactNode
  /** Overrides the "Why?" affordance label. */
  whyLabel?: string
  className?: string
  'data-testid'?: string
}

/**
 * Progressive-disclosure help: one plain sentence up front plus a (?) "Why?"
 * `InfoHint` that reveals the fuller detail on demand. The detail is not
 * rendered until requested, and the hint never blocks the primary action or
 * navigation around it.
 */
export const InlineHelp = ({
  text,
  detail,
  whyLabel,
  className,
  'data-testid': testId,
}: InlineHelpProps) => {
  const { t } = useTranslation()

  return (
    <div className={cn(['text-sm text-text-secondary', className])} data-testid={testId}>
      <p className="flex flex-wrap items-center gap-x-1.5">
        {text != null && <span>{text}</span>}
        <InfoHint
          text={detail}
          label={whyLabel ?? t('common.why')}
          triggerTestId={testId ? `${testId}-why` : undefined}
          contentTestId={testId ? `${testId}-detail` : undefined}
        />
      </p>
    </div>
  )
}
